/**
 * uploadAttachment — shared presign → PUT → finalize upload utility.
 *
 * Encapsulates the three-step R2 upload flow used wherever the app
 * needs to upload a file to object storage via a signed URL:
 *
 *   1. POST /api/attachments/presign   → { url, r2Key, id }
 *   2. PUT  <url>                      → raw fetch (NO auth headers — R2 HMAC)
 *   3. POST /api/attachments/finalize  → returns the finalized attachment record
 *
 * The caller is responsible for any post-upload steps (e.g. linking the
 * attachment to a session, invalidating a cache, etc.).
 */
import { apiFetch } from '@/lib/api';

export interface UploadAttachmentOptions {
  /** Human-readable title stored on the attachment record (defaults to filename without extension). */
  title?: string;
  /** Tags to associate with the attachment record. */
  tags?: string[];
}

export interface UploadAttachmentResult {
  /** The finalized attachment id — use this to reference the upload. */
  attachmentId: string;
  /** The R2 object key — needed by some callers that store it directly. */
  r2Key: string;
  /**
   * Raw body returned by /api/attachments/finalize (parsed JSON).
   * Exposed for callers (e.g. AttachmentsService) that need to map
   * the full attachment record without a second HTTP round-trip.
   */
  finalizeData: unknown;
}

/**
 * Upload a File through the platform's presign → PUT → finalize pipeline.
 *
 * @throws If any of the three HTTP steps fails (non-2xx response).
 *
 * @example
 * ```ts
 * const { attachmentId } = await uploadAttachment(file);
 * await someService.linkAttachment(thingId, attachmentId);
 * ```
 */
export async function uploadAttachment(
  file: File,
  options: UploadAttachmentOptions = {},
): Promise<UploadAttachmentResult> {
  const contentType = file.type || 'application/octet-stream';

  // Step 1: presign — get a short-lived signed PUT URL from the platform.
  const presignRes = await apiFetch('/api/attachments/presign', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, contentType }),
  });
  if (!presignRes.ok) {
    throw new Error(`presign failed: ${presignRes.status}`);
  }
  const presigned = await presignRes.json();
  const { url: putUrl, r2Key, id: attachmentId } = presigned.data ?? {};

  if (!putUrl || !r2Key || !attachmentId) {
    throw new Error('presign response missing required fields (url, r2Key, id)');
  }

  // Step 2: PUT to R2.
  // IMPORTANT: use plain fetch — NOT apiFetch. The presigned URL contains an
  // HMAC signature computed for the exact set of request headers. Adding any
  // extra header (e.g. Authorization / X-Organization-Id from apiFetch) would
  // invalidate the signature and cause R2 to return 403.
  const putRes = await fetch(putUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!putRes.ok) {
    throw new Error(`storage PUT failed: ${putRes.status}`);
  }

  // Step 3: finalize — register the upload in the platform DB.
  const finRes = await apiFetch('/api/attachments/finalize', {
    method: 'POST',
    body: JSON.stringify({
      id: attachmentId,
      title: options.title ?? file.name.replace(/\.[^.]+$/, ''),
      tags: options.tags ?? [],
      r2Key,
      originalFilename: file.name,
      size: file.size,
      mimeType: contentType,
    }),
  });
  if (!finRes.ok) {
    throw new Error(`finalize failed: ${finRes.status}`);
  }

  const finalizeData = await finRes.json();

  return { attachmentId, r2Key, finalizeData };
}
